import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  type CartItem,
  type ShopifyProduct,
  createShopifyCart,
  addLineToShopifyCart,
  updateShopifyCartLine,
  removeLineFromShopifyCart,
  updateShopifyCartDiscount,
  storefrontApiRequest,
  CART_QUERY,
  formatCheckoutUrl,
} from '@/lib/shopify';
import { trackAddToCart } from '@/lib/meta-pixel';
import { gtagAddToCart } from '@/lib/google-analytics';
import { ttqAddToCart } from '@/lib/tiktok-pixel';
import { klaviyoTrackAddedToCart } from '@/lib/klaviyo';
import { trackAddToCart as trackAddToCartInternal, setCartItemsForAbandonment } from '@/lib/analytics';
import { cartItemCount } from '@/lib/cartAbandonment';
let _qtyTimer: ReturnType<typeof setTimeout> | null = null;

const fireInternalAtc = (item: { variantId: string; variantTitle?: string; product: { node: { title: string } }; price: { amount: string; currencyCode?: string }; quantity: number }) => {
  // Extract bundle id from variantTitle suffix (e.g. "OG - 2 Bottles" → "2-bottles")
  const bundleLabel = (item.variantTitle || "").split(" - ").slice(1).join(" - ").trim();
  trackAddToCartInternal({
    product_id: item.variantId,
    product_name: item.product.node.title,
    value: parseFloat(item.price.amount) * item.quantity,
    currency: item.price.currencyCode || 'USD',
    num_items: item.quantity,
    element_label: bundleLabel || undefined,
  });
};

const fireKlaviyoAtc = (item: { variantId: string; product: { node: { title: string } }; price: { amount: string; currencyCode?: string }; quantity: number }) => {
  klaviyoTrackAddedToCart({
    product_id: item.variantId,
    product_name: item.product.node.title,
    price: parseFloat(item.price.amount),
    quantity: item.quantity,
    currency: item.price.currencyCode || 'USD',
  });
};


interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  discountCode: string | null;
  isOpen: boolean;
  isLoading: boolean;
  isSyncing: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (item: Omit<CartItem, 'lineId'>, discountCode?: string | null) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  getCheckoutUrl: () => string | null;
  itemCount: () => number;
  total: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      discountCode: null,
      isOpen: false,
      isLoading: false,
      isSyncing: false,

      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),

      addItem: async (item, discountCode = null) => {
        const { items, cartId, discountCode: currentDiscount, clearCart } = get();
        const existingItem = items.find(i => i.variantId === item.variantId);

        set({ isLoading: true });
        try {
          if (!cartId) {
            const result = await createShopifyCart({ ...item, lineId: null }, discountCode);
            if (result) {
              set({
                cartId: result.cartId,
                checkoutUrl: result.checkoutUrl,
                discountCode: discountCode ?? null,
                items: [{ ...item, lineId: result.lineId }],
                isOpen: true,
              });
              trackAddToCart({
                content_name: item.product.node.title,
                content_ids: [item.variantId],
                content_type: "product",
                value: parseFloat(item.price.amount) * item.quantity,
                currency: item.price.currencyCode || "USD",
              });
              gtagAddToCart({
                item_id: item.variantId,
                item_name: item.product.node.title,
                price: parseFloat(item.price.amount),
                quantity: item.quantity,
                currency: item.price.currencyCode || "USD",
              });
              ttqAddToCart({
                content_name: item.product.node.title,
                content_id: item.variantId,
                content_type: "product",
                value: parseFloat(item.price.amount) * item.quantity,
                currency: item.price.currencyCode || "USD",
                quantity: item.quantity,
              });
              fireInternalAtc(item);
              fireKlaviyoAtc(item);
            }
          } else if (existingItem) {
            if ((discountCode ?? null) !== (currentDiscount ?? null)) {
              const dRes = await updateShopifyCartDiscount(cartId, discountCode ?? null);
              if (dRes.cartNotFound) { clearCart(); return; }
              if (dRes.success) set({ discountCode: discountCode ?? null });
            }
            const newQuantity = existingItem.quantity + item.quantity;
            if (!existingItem.lineId) return;
            const result = await updateShopifyCartLine(cartId, existingItem.lineId, newQuantity);
            if (result.success) {
              set({ items: get().items.map(i => i.variantId === item.variantId ? { ...i, quantity: newQuantity } : i), isOpen: true });
              trackAddToCart({
                content_name: item.product.node.title,
                content_ids: [item.variantId],
                content_type: "product",
                value: parseFloat(item.price.amount) * item.quantity,
                currency: item.price.currencyCode || "USD",
              });
              gtagAddToCart({
                item_id: item.variantId,
                item_name: item.product.node.title,
                price: parseFloat(item.price.amount),
                quantity: item.quantity,
                currency: item.price.currencyCode || "USD",
              });
              ttqAddToCart({
                content_name: item.product.node.title,
                content_id: item.variantId,
                content_type: "product",
                value: parseFloat(item.price.amount) * item.quantity,
                currency: item.price.currencyCode || "USD",
                quantity: item.quantity,
              });
              fireInternalAtc(item);
              fireKlaviyoAtc(item);
            } else if (result.cartNotFound) clearCart();
          } else {
            if ((discountCode ?? null) !== (currentDiscount ?? null)) {
              const dRes = await updateShopifyCartDiscount(cartId, discountCode ?? null);
              if (dRes.cartNotFound) { clearCart(); return; }
              if (dRes.success) set({ discountCode: discountCode ?? null });
            }
            const result = await addLineToShopifyCart(cartId, { ...item, lineId: null });
            if (result.success) {
              set({ items: [...get().items, { ...item, lineId: result.lineId ?? null }], isOpen: true });
              trackAddToCart({
                content_name: item.product.node.title,
                content_ids: [item.variantId],
                content_type: "product",
                value: parseFloat(item.price.amount) * item.quantity,
                currency: item.price.currencyCode || "USD",
              });
              gtagAddToCart({
                item_id: item.variantId,
                item_name: item.product.node.title,
                price: parseFloat(item.price.amount),
                quantity: item.quantity,
                currency: item.price.currencyCode || "USD",
              });
              ttqAddToCart({
                content_name: item.product.node.title,
                content_id: item.variantId,
                content_type: "product",
                value: parseFloat(item.price.amount) * item.quantity,
                currency: item.price.currencyCode || "USD",
                quantity: item.quantity,
              });
              fireInternalAtc(item);
              fireKlaviyoAtc(item);
            } else if (result.cartNotFound) clearCart();
          }
        } catch (error) {
          console.error('Failed to add item:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) { await get().removeItem(variantId); return; }
        const { items, cartId, clearCart, discountCode: currentDiscount } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        // Optimistic UI update — show new quantity immediately
        // Pricing: match Shopify — 25% off for 2 ($67.50), 40% off for 3 ($81.00)
        const UNIT = 45;
        let totalPrice: number;
        let newDiscountCode: string | null;
        let compareAtTotal: number | undefined;
        if (quantity === 1) { totalPrice = 45; newDiscountCode = null; compareAtTotal = undefined; }
        else if (quantity === 2) { totalPrice = 67.50; newDiscountCode = "OARIA_BUNDLE2"; compareAtTotal = 90; }
        else { totalPrice = 81.00; newDiscountCode = "OARIA_BUNDLE3"; compareAtTotal = 135; }
        const newUnitPrice = totalPrice / quantity;
        const newOriginalPrice = compareAtTotal ? { amount: (compareAtTotal / quantity).toFixed(2), currencyCode: item.price.currencyCode } : undefined;

        set({ items: get().items.map(i => i.variantId === variantId ? {
          ...i,
          quantity,
          price: { amount: newUnitPrice.toFixed(2), currencyCode: i.price.currencyCode },
          originalPrice: newOriginalPrice,
        } : i) });

        // Debounce API call — if user clicks +/- rapidly, only final quantity is sent
        if (_qtyTimer) clearTimeout(_qtyTimer);
        _qtyTimer = setTimeout(async () => {
          _qtyTimer = null;
          const cur = get();
          const curItem = cur.items.find(i => i.variantId === variantId);
          if (!curItem?.lineId || !cur.cartId) return;
          set({ isLoading: true });
          try {
            // Update discount code if changed
            if (newDiscountCode !== (cur.discountCode ?? null)) {
              const dRes = await updateShopifyCartDiscount(cur.cartId, newDiscountCode);
              if (dRes.cartNotFound) { cur.clearCart(); return; }
              if (dRes.success) set({ discountCode: newDiscountCode });
            }
            const result = await updateShopifyCartLine(cur.cartId, curItem.lineId, quantity);
            if (result.cartNotFound) cur.clearCart();
          } catch (error) { console.error('Failed to update quantity:', error); }
          finally { set({ isLoading: false }); }
        }, 350);
      },

      removeItem: async (variantId) => {
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        set({ isLoading: true });
        try {
          const result = await removeLineFromShopifyCart(cartId, item.lineId);
          if (result.success) {
            const newItems = get().items.filter(i => i.variantId !== variantId);
            newItems.length === 0 ? clearCart() : set({ items: newItems });
          } else if (result.cartNotFound) clearCart();
        } catch (error) { console.error('Failed to remove item:', error); }
        finally { set({ isLoading: false }); }
      },
      clearCart: () => { set({ items: [], cartId: null, checkoutUrl: null, discountCode: null }); },
      getCheckoutUrl: () => {
        const url = get().checkoutUrl;
        return url ? formatCheckoutUrl(url) : null;
      },
      itemCount: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
      total: () => get().items.reduce((sum, i) => sum + parseFloat(i.price.amount) * i.quantity, 0),

      syncCart: async () => {
        const { cartId, isSyncing, clearCart } = get();
        if (!cartId || isSyncing) return;
        set({ isSyncing: true });
        try {
          const data = await storefrontApiRequest(CART_QUERY, { id: cartId });
          if (!data) return;
          const cart = data?.data?.cart;
          if (!cart || cart.totalQuantity === 0) clearCart();
        } catch (error) { console.error('Failed to sync cart:', error); }
        finally { set({ isSyncing: false }); }
      },
    }),
    {
      name: 'shopify-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items, cartId: state.cartId, checkoutUrl: state.checkoutUrl, discountCode: state.discountCode }),
    }
  )
);

let lastAbandonmentItemCount = -1;
const syncAbandonmentItemCount = (items: CartItem[]) => {
  const count = cartItemCount(items);
  if (count === lastAbandonmentItemCount) return;
  lastAbandonmentItemCount = count;
  setCartItemsForAbandonment(count);
};

useCartStore.subscribe((state) => syncAbandonmentItemCount(state.items));
syncAbandonmentItemCount(useCartStore.getState().items);
